/**
 * 工作流详情页面
 * 展示工作流信息、动态表单、备注输入和历史提交记录
 * 所有表单组件（包括上传）完全由后端 form_components 配置驱动
 */

import type { ReactElement } from 'react'
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  ArrowLeft,
  CreditCard,
  Loader2,
  LogIn,
  Server,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuthStore } from '@/stores'
import { useWorkflowDetail } from '@/hooks/useWorkflows'
import { useWorkflowForm } from '@/hooks/useWorkflowForm'
import { useBeforeUnload } from '@/hooks/useBeforeUnload'
import { useDashboardStats } from '@/hooks/useDashboardStats'
import {
  useExclusiveSettings,
  useExclusiveServers,
  useStartExclusiveServers,
} from '@/hooks/useExclusiveCompute'
import { submitBatchTask } from '@/services/taskService'
import {
  DynamicFormRenderer,
  CostCalculator,
  RemarkInput,
  SubmissionHistory,
  UploadStatusPanel,
} from '@/components/workflow'
import type { SubmissionHistoryRef } from '@/components/workflow/SubmissionHistory'
import {
  hasRestrictedHighCostSelections,
  COMPONENT_TYPES,
} from '@/types/workflow'
import type { WorkflowDetail as WorkflowDetailType } from '@/types/workflow'
import type { ComputeType } from '@/types/auth'
import { cn } from '@/lib/utils'
import {
  isNativeMultiOutputWorkflow,
  resolveNativeOutputMultiplier,
} from '@/lib/nativeMultiOutputWorkflow'

const SHARED_POOL_BUSY_TASK_THRESHOLD = 50
const EXCLUSIVE_SERVER_BILLING_POWER = 100
const MAX_REPEAT_SUBMIT_COUNT = 20
const SHARED_USER_RESTRICTED_OPTION_MESSAGE = '该模式耗费大量算力，仅对专享用户开放'

/**
 * 计算专享模式执行任务所需的最少设备数
 * @param shouldStartExclusiveServers 是否需要先开机专享设备
 * @param isStartCountValid 开机数量是否有效
 * @param startCount 计划开机数量
 * @param runningServerCount 当前可用中的专享设备数
 * @returns 执行任务需要覆盖的设备数量
 */
function getExclusiveRequiredServerCount(
  shouldStartExclusiveServers: boolean,
  isStartCountValid: boolean,
  startCount: number,
  runningServerCount: number
): number {
  if (shouldStartExclusiveServers && isStartCountValid) {
    return Math.max(startCount, 1)
  }

  return Math.max(runningServerCount, 1)
}

/**
 * 从表单状态中提取图片 URL 列表
 * @param formState 表单状态
 * @param components 表单组件配置
 * @returns 图片 URL 列表
 */
function extractImageUrls(
  formState: Record<string, unknown>,
  components: WorkflowDetailType['form_components']
): string[] {
  const urls: string[] = []

  components.forEach((component) => {
    const value = formState[component.param_name]

    if (component.component_type === COMPONENT_TYPES.MULTI_IMAGE_UPLOAD) {
      if (Array.isArray(value)) {
        urls.push(...(value as string[]))
      }
    } else if (component.component_type === COMPONENT_TYPES.SINGLE_IMAGE_UPLOAD) {
      if (typeof value === 'string' && value !== '') {
        urls.push(value)
      }
    }
  })

  return urls
}

/**
 * 工作流详情页组件
 * @returns 页面 ReactElement
 */
export function WorkflowDetailPage(): ReactElement {
  const navigate = useNavigate()
  const { workflowId } = useParams<{ workflowId: string }>()

  // 用户状态
  const user = useAuthStore((state) => state.user)
  const credits = user?.company?.balance ?? 0
  const computeType = user?.company?.computeType ?? 'shared'
  const isExclusiveUser = computeType === 'exclusive'
  const shouldRestrictHighCostOptions = user !== null && !isExclusiveUser
  const shouldLoadDashboardStats = user !== null && !isExclusiveUser
  const dashboardStatsQuery = useDashboardStats(shouldLoadDashboardStats)
  const sharedPoolTaskCount = dashboardStatsQuery.data?.shared_pool_task_count ?? 0
  const exclusiveSettingsQuery = useExclusiveSettings(isExclusiveUser)

  // 页面关闭警告（当有正在进行的上传时）
  useBeforeUnload()

  // 工作流详情
  const {
    data: workflow,
    isLoading: isLoadingWorkflow,
    error: workflowError,
  } = useWorkflowDetail(workflowId)
  const formComponents = useMemo(
    (): WorkflowDetailType['form_components'] => workflow?.form_components ?? [],
    [workflow?.form_components]
  )

  // 表单状态（图片数量自动从表单中计算）
  const {
    formState,
    errors: formErrors,
    updateField,
    validate,
    resetForm,
    calculatedCost,
    imageCount,
    promptCount,
  } = useWorkflowForm(
    formComponents,
    workflow?.power_consumption ?? 0,
    workflow?.enable_multi_prompt ?? false,
    shouldRestrictHighCostOptions
  )

  // 任务状态
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // 备注状态
  const [remark, setRemark] = useState('')

  // 算力模式状态（专享用户默认专享，共享用户固定共享）
  const defaultComputeMode: ComputeType = isExclusiveUser ? 'exclusive' : 'shared'
  const [selectedComputeMode, setSelectedComputeMode] = useState<ComputeType>(defaultComputeMode)
  const [exclusiveStartCountInput, setExclusiveStartCountInput] = useState('1')
  const [repeatSubmitCountInput, setRepeatSubmitCountInput] = useState('1')
  const canCheckExclusiveServers = isExclusiveUser && selectedComputeMode === 'exclusive'
  const exclusiveServersQuery = useExclusiveServers(canCheckExclusiveServers)
  const startExclusiveServersMutation = useStartExclusiveServers()

  // 历史记录组件 ref（用于刷新）
  const historyRef = useRef<SubmissionHistoryRef>(null)

  useEffect(() => {
    setSelectedComputeMode(defaultComputeMode)
  }, [defaultComputeMode])

  // 计算权限状态
  const needsLogin = user === null
  const shouldShowSharedQueueBusyHint =
    !needsLogin &&
    !isExclusiveUser &&
    sharedPoolTaskCount > SHARED_POOL_BUSY_TASK_THRESHOLD
  const hasRestrictedSelections = shouldRestrictHighCostOptions &&
    workflow !== undefined &&
    hasRestrictedHighCostSelections(formComponents, formState)
  const isSharedMode = selectedComputeMode === 'shared'
  const runningExclusiveServerCount = (exclusiveServersQuery.data?.list ?? []).filter((server) => (
    server.power_state === 'running' || server.power_state === 'starting'
  )).length
  const hasRunningExclusiveServer = runningExclusiveServerCount > 0
  const maxExclusiveStartCount = exclusiveSettingsQuery.data?.maxRunning ?? 0
  const parsedExclusiveStartCount = Number(exclusiveStartCountInput)
  const isExclusiveStartCountValid =
    Number.isInteger(parsedExclusiveStartCount) &&
    parsedExclusiveStartCount >= 1 &&
    parsedExclusiveStartCount <= maxExclusiveStartCount
  const parsedRepeatSubmitCount = Number(repeatSubmitCountInput)
  const isRepeatSubmitCountValid =
    Number.isInteger(parsedRepeatSubmitCount) &&
    parsedRepeatSubmitCount >= 1 &&
    parsedRepeatSubmitCount <= MAX_REPEAT_SUBMIT_COUNT
  const repeatSubmitCount = isRepeatSubmitCountValid ? parsedRepeatSubmitCount : 1
  const isNativeMultiOutput = isNativeMultiOutputWorkflow(workflow?.id)
  const nativeOutputMultiplier = useMemo(
    () => resolveNativeOutputMultiplier(workflow, formState),
    [workflow, formState]
  )
  const nativeExpectedOutputCount = imageCount * promptCount * nativeOutputMultiplier
  const effectiveRepeatSubmitCount = isNativeMultiOutput ? 1 : repeatSubmitCount
  const isEffectiveRepeatSubmitCountValid = isNativeMultiOutput || isRepeatSubmitCountValid
  const effectiveCalculatedCost = isNativeMultiOutput
    ? calculatedCost * nativeOutputMultiplier
    : calculatedCost
  const isCheckingExclusiveServer = canCheckExclusiveServers &&
    (exclusiveSettingsQuery.isLoading || exclusiveServersQuery.isLoading)
  const shouldShowExclusivePowerOnHint =
    isExclusiveUser &&
    selectedComputeMode === 'exclusive' &&
    !isCheckingExclusiveServer &&
    !hasRunningExclusiveServer
  const exclusiveRequiredServerCount = getExclusiveRequiredServerCount(
    shouldShowExclusivePowerOnHint,
    isExclusiveStartCountValid,
    parsedExclusiveStartCount,
    runningExclusiveServerCount
  )
  const exclusiveRequiredCredits = exclusiveRequiredServerCount * EXCLUSIVE_SERVER_BILLING_POWER
  const sharedRequiredCredits = effectiveCalculatedCost * effectiveRepeatSubmitCount
  const isCreditEnough = isSharedMode
    ? credits >= sharedRequiredCredits
    : credits >= exclusiveRequiredCredits
  const canSubmit =
    !needsLogin &&
    isCreditEnough &&
    !isSubmitting &&
    !startExclusiveServersMutation.isPending &&
    !isCheckingExclusiveServer &&
    !hasRestrictedSelections &&
    isEffectiveRepeatSubmitCountValid &&
    (!shouldShowExclusivePowerOnHint || isExclusiveStartCountValid)
  const handleComputeModeChange = useCallback((mode: ComputeType): void => {
    setSelectedComputeMode(mode)
    setSubmitError(null)
  }, [])

  /**
   * 处理任务提交
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    if (workflow === undefined || !canSubmit) {
      return
    }

    // 验证表单
    if (!validate()) {
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)
    let shouldResetForm = false

    try {
      if (shouldShowExclusivePowerOnHint) {
        if (!isExclusiveStartCountValid) {
          setSubmitError(
            maxExclusiveStartCount > 0
              ? `开机设备数需在 1-${String(maxExclusiveStartCount)} 台之间`
              : '专享设备配额加载中，请稍后重试'
          )
          return
        }

        const startResult = await startExclusiveServersMutation.mutateAsync(
          {
            count: Math.floor(parsedExclusiveStartCount),
            mode: 'target',
          }
        )

        if (startResult.queuedCount > 0) {
          toast.success(`已提交 ${String(startResult.queuedCount)} 台设备开机请求`)
        } else {
          toast.info('当前目标台数已满足，无需新增开机')
        }
      }

      // 从表单状态中提取图片 URL
      const imageUrls = extractImageUrls(formState, formComponents)

      // 构建提交参数
      const submitParams: Parameters<typeof submitBatchTask>[0] = {
        workflow_id: workflow.id,
        image_urls: imageUrls,
        form_data: { ...formState, remark },
        repeat_count: effectiveRepeatSubmitCount,
        useSharedIfNoExclusive: selectedComputeMode === 'shared',
      }

      const submitResult = await submitBatchTask(submitParams)
      shouldResetForm = true

      if (submitResult.routing_status === 'waiting_for_capacity') {
        toast.warning('设备启动中（约3分钟）')
        if (submitResult.estimated_ready_at !== undefined && submitResult.estimated_ready_at !== '') {
          toast.info(`预计可用时间：${new Date(submitResult.estimated_ready_at).toLocaleString('zh-CN')}`)
        }
        void navigate(`/records/generations/${submitResult.id}`)
      } else {
        const submittedOutputCount = submitResult.expected_output_count ?? submitResult.total_count
        toast.success(`已提交批量任务，共 ${String(submittedOutputCount)} 张结果`)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '提交失败，请重试'
      setSubmitError(message)
    } finally {
      if (shouldResetForm) {
        historyRef.current?.refresh()
        // 提交成功后延迟重置表单（避免大量 DOM 节点同步移除导致动画冲突）
        setTimeout(() => {
          resetForm()
          setRemark('')
        }, 0)
      }

      setIsSubmitting(false)
    }
  }, [
    workflow,
    formComponents,
    canSubmit,
    validate,
    formState,
    remark,
    resetForm,
    selectedComputeMode,
    effectiveRepeatSubmitCount,
    shouldShowExclusivePowerOnHint,
    isExclusiveStartCountValid,
    maxExclusiveStartCount,
    parsedExclusiveStartCount,
    startExclusiveServersMutation,
    navigate,
  ])

  // 加载中状态
  if (isLoadingWorkflow) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  // 加载错误或工作流不存在
  if (workflowError !== null || workflow === undefined) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="icon">
            <Link to="/workflows" aria-label="返回工作流列表">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">工作流不存在</h1>
        </div>

        <div className="rounded-2xl border bg-card p-10 text-center">
          <p className="text-sm text-muted-foreground">
            找不到该工作流，可能是链接已过期或工作流已下线。
          </p>
          <div className="mt-4">
            <Button asChild>
              <Link to="/workflows">返回工作流列表</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面头部 */}
      <PageHeader workflow={workflow} />

      {/* 主内容区 - 左右分栏（表单2:历史记录3） */}
      <div className="grid gap-6 lg:grid-cols-5">
        {/* 左侧：表单区域（紧凑） */}
        <div className="space-y-4 lg:col-span-2">
          {/* 表单卡片：备注 + 动态表单 */}
          <div className="rounded-2xl border bg-card p-5 shadow-sm">
            {/* 备注输入 */}
            <RemarkInput
              value={remark}
              onChange={setRemark}
              disabled={isSubmitting}
            />

            {/* 分割线 */}
            {formComponents.length > 0 && (
              <div className="my-4 border-t" />
            )}

            {/* 动态表单 */}
            {formComponents.length > 0 ? (
              <DynamicFormRenderer
                components={formComponents}
                formState={formState}
                errors={formErrors}
                onChange={updateField}
                disabled={isSubmitting}
                showGroupTitles={false}
                userCredits={needsLogin || isExclusiveUser ? undefined : credits}
                basePower={workflow.power_consumption}
                enableMultiPrompt={workflow.enable_multi_prompt}
                restrictHighCostOptions={shouldRestrictHighCostOptions}
                restrictedOptionMessage={SHARED_USER_RESTRICTED_OPTION_MESSAGE}
              />
            ) : (
              <div className="py-4 text-center text-sm text-muted-foreground">
                该工作流暂无可配置参数
              </div>
            )}
          </div>
        </div>

        {/* 右侧：费用预估 + 提交按钮 + 历史记录 */}
        <div className="space-y-4 lg:col-span-3">
          {/* 费用预估和提交按钮 */}
          <div className="rounded-2xl border bg-card p-5 shadow-sm">
            <div className="space-y-4">
              {!needsLogin && (
                <ComputeModeSection
                  selectedMode={selectedComputeMode}
                  onModeChange={handleComputeModeChange}
                  isExclusiveUser={isExclusiveUser}
                  disabled={isSubmitting}
                />
              )}

              {/* 费用预估 */}
              {selectedComputeMode !== 'exclusive' && (
                <CostCalculator
                  basePower={workflow.power_consumption}
                  totalCost={effectiveCalculatedCost}
                  imageCount={imageCount}
                  promptCount={promptCount}
                  repeatCount={effectiveRepeatSubmitCount}
                  userPower={credits}
                  formComponents={formComponents}
                  formState={formState}
                  embedded
                  enableMultiPrompt={workflow.enable_multi_prompt}
                  nativeOutputMultiplier={isNativeMultiOutput ? nativeOutputMultiplier : 1}
                  {...(isNativeMultiOutput ? { expectedOutputCount: nativeExpectedOutputCount } : {})}
                />
              )}

              {!needsLogin && !isExclusiveUser && (
                shouldShowSharedQueueBusyHint ? (
                  <SharedQueueBusyUpgradeHint sharedPoolTaskCount={sharedPoolTaskCount} />
                ) : (
                  <SharedComputeUpgradeHint />
                )
              )}

              {shouldShowExclusivePowerOnHint && (
                <ExclusiveComputePowerOnHint
                  startCountInput={exclusiveStartCountInput}
                  maxStartCount={maxExclusiveStartCount}
                  isStartCountValid={isExclusiveStartCountValid}
                  disabled={isSubmitting}
                  onStartCountChange={(value): void => {
                    setExclusiveStartCountInput(value)
                    setSubmitError(null)
                  }}
                />
              )}

              {/* 提交按钮 */}
              <SubmitSection
                needsLogin={needsLogin}
                isCreditEnough={isCreditEnough}
                canSubmit={canSubmit}
                isSubmitting={isSubmitting}
                submitError={submitError}
                selectedComputeMode={selectedComputeMode}
                willStartExclusiveServers={shouldShowExclusivePowerOnHint}
                repeatSubmitCount={repeatSubmitCount}
                repeatSubmitCountInput={repeatSubmitCountInput}
                isRepeatSubmitCountValid={isRepeatSubmitCountValid}
                maxRepeatSubmitCount={MAX_REPEAT_SUBMIT_COUNT}
                showRepeatSubmitCount={!isNativeMultiOutput}
                generationSourceType={workflow.generation_source_type}
                restrictedSelectionMessage={
                  hasRestrictedSelections ? SHARED_USER_RESTRICTED_OPTION_MESSAGE : null
                }
                onRepeatSubmitCountChange={(value): void => {
                  setRepeatSubmitCountInput(value)
                  setSubmitError(null)
                }}
                onSubmit={(): void => { void handleSubmit() }}
                onNavigate={navigate}
              />
            </div>
          </div>

          {/* 历史提交记录 */}
          <SubmissionHistory
            ref={historyRef}
            workflowId={workflowId}
            maxItems={10}
            isLoggedIn={!needsLogin}
          />
        </div>
      </div>

      {/* 浮动上传进度面板 */}
      <UploadStatusPanel />
    </div>
  )
}

// ==================== 子组件 ====================

/**
 * 页面头部组件属性
 */
interface PageHeaderProps {
  workflow: WorkflowDetailType
}

/**
 * 页面头部组件
 */
function PageHeader({ workflow }: PageHeaderProps): ReactElement {
  return (
    <div className="flex items-start gap-4">
      <Button asChild variant="ghost" size="icon">
        <Link to="/workflows" aria-label="返回工作流列表">
          <ArrowLeft className="h-4 w-4" />
        </Link>
      </Button>
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold tracking-tight">{workflow.title}</h1>
          <span className="rounded-full bg-secondary px-2 py-1 text-xs text-secondary-foreground/80">
            {workflow.power_consumption} 算力 / 次
          </span>
          {workflow.enable_multi_prompt && (
            <span className="rounded-full bg-primary/10 px-2 py-1 text-xs text-primary">
              支持多组提示词生成
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{workflow.description}</p>
      </div>
    </div>
  )
}

/**
 * 专享设备未开机提示组件属性
 */
interface ExclusiveComputePowerOnHintProps {
  startCountInput: string
  maxStartCount: number
  isStartCountValid: boolean
  disabled: boolean
  onStartCountChange: (value: string) => void
}

/**
 * 专享设备未开机提示组件
 */
function ExclusiveComputePowerOnHint({
  startCountInput,
  maxStartCount,
  isStartCountValid,
  disabled,
  onStartCountChange,
}: ExclusiveComputePowerOnHintProps): ReactElement {
  const displayMaxStartCount = Math.max(1, maxStartCount)

  return (
    <div className="space-y-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
      <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-300">
        <Server className="h-4 w-4" />
        请先开机专享设备
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        当前没有可用设备（开机中视为可用，关机中不计入），可先输入开机数量后直接提交任务。
      </p>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="exclusive-start-count" className="text-xs font-medium text-amber-700 dark:text-amber-300">
            开机设备数
          </Label>
          <span className="text-xs text-muted-foreground">最多 {displayMaxStartCount} 台</span>
        </div>
        <Input
          id="exclusive-start-count"
          type="number"
          inputMode="numeric"
          min={1}
          max={displayMaxStartCount}
          step={1}
          value={startCountInput}
          disabled={disabled}
          onChange={(event): void => {
            onStartCountChange(event.target.value)
          }}
          className={cn(
            'h-9 border-amber-500/40 bg-background text-sm',
            !isStartCountValid && 'border-destructive/60'
          )}
        />
        {!isStartCountValid && (
          <p className="text-xs text-destructive">请输入 1 到 {displayMaxStartCount} 之间的整数</p>
        )}
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        专享计费：每台服务器每 10 分钟预扣 100 算力。余额不足时会停止派发新专享任务，
        正在执行的任务完成后自动关机，提交任务将回退共享池计费。
      </p>
    </div>
  )
}

/**
 * 共享算力升级提示
 */
function SharedComputeUpgradeHint(): ReactElement {
  return (
    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-500/12 text-emerald-600 dark:text-emerald-400">
          <Server className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-medium">开通专享算力，可独享设备资源</p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            共享池会和其他用户一起排队，升级到专享算力后可获得更稳定、更可控的生成体验。
          </p>
        </div>
        <Button asChild type="button" size="sm" variant="outline" className="h-8 shrink-0 px-2.5">
          <Link to="/pricing">去开通</Link>
        </Button>
      </div>
    </div>
  )
}

/**
 * 共享算力拥堵升级提示
 */
interface SharedQueueBusyUpgradeHintProps {
  sharedPoolTaskCount: number
}

/**
 * 共享算力拥堵升级提示
 */
function SharedQueueBusyUpgradeHint({
  sharedPoolTaskCount,
}: SharedQueueBusyUpgradeHintProps): ReactElement {
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-500/12 text-amber-600 dark:text-amber-400">
          <Server className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
            当前共享算力服务器任务较多
          </p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            当前共享算力服务器任务比较多（约 {sharedPoolTaskCount} 个），生图任务需要排队等待，
            建议升级到专享服务器。
          </p>
        </div>
        <Button asChild type="button" size="sm" variant="outline" className="h-8 shrink-0 px-2.5">
          <Link to="/pricing">去开通</Link>
        </Button>
      </div>
    </div>
  )
}

/**
 * 提交区域组件属性
 */
interface SubmitSectionProps {
  needsLogin: boolean
  isCreditEnough: boolean
  canSubmit: boolean
  isSubmitting: boolean
  submitError: string | null
  selectedComputeMode: ComputeType
  willStartExclusiveServers: boolean
  repeatSubmitCount: number
  repeatSubmitCountInput: string
  isRepeatSubmitCountValid: boolean
  maxRepeatSubmitCount: number
  showRepeatSubmitCount: boolean
  generationSourceType: WorkflowDetailType['generation_source_type']
  restrictedSelectionMessage: string | null
  onRepeatSubmitCountChange: (value: string) => void
  onSubmit: () => void
  onNavigate: (path: string) => void | Promise<void>
}

/**
 * 提交区域组件
 */
function SubmitSection({
  needsLogin,
  isCreditEnough,
  canSubmit,
  isSubmitting,
  submitError,
  selectedComputeMode,
  willStartExclusiveServers,
  repeatSubmitCount,
  repeatSubmitCountInput,
  isRepeatSubmitCountValid,
  maxRepeatSubmitCount,
  showRepeatSubmitCount,
  generationSourceType,
  restrictedSelectionMessage,
  onRepeatSubmitCountChange,
  onSubmit,
  onNavigate,
}: SubmitSectionProps): ReactElement {
  if (needsLogin) {
    return (
      <Button
        type="button"
        size="lg"
        className="w-full"
        onClick={(): void => {
          void onNavigate('/login')
        }}
      >
        <LogIn className="h-4 w-4" />
        登录后使用
      </Button>
    )
  }

  const repeatSubmitHint = isRepeatSubmitCountValid
    ? repeatSubmitCount > 1
      ? generationSourceType === 'text_to_image'
        ? `每条提示词生成 ${String(repeatSubmitCount)} 张`
        : `每张原图生成 ${String(repeatSubmitCount)} 张`
      : generationSourceType === 'text_to_image'
        ? '默认每条提示词生成 1 张'
        : '默认每张原图生成 1 张'
    : `请输入 1 到 ${String(maxRepeatSubmitCount)} 之间的整数`

  return (
    <div className="space-y-2">
      <div
        className={cn(
          'grid gap-3 sm:items-end',
          showRepeatSubmitCount && 'sm:grid-cols-[120px_minmax(0,1fr)]'
        )}
      >
        {showRepeatSubmitCount && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="repeat-submit-count" className="text-xs font-medium">
                重复生成
              </Label>
              <span className="text-xs text-muted-foreground">最多 {String(maxRepeatSubmitCount)} 次</span>
            </div>
            <Input
              id="repeat-submit-count"
              type="number"
              inputMode="numeric"
              min={1}
              max={maxRepeatSubmitCount}
              step={1}
              value={repeatSubmitCountInput}
              disabled={isSubmitting}
              onChange={(event): void => {
                onRepeatSubmitCountChange(event.target.value)
              }}
              className={cn(
                'h-11 text-sm',
                !isRepeatSubmitCountValid && 'border-destructive/60'
              )}
            />
          </div>
        )}

        {isCreditEnough ? (
          <Button
            type="button"
            size="lg"
            className="w-full"
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                提交中...
              </>
            ) : selectedComputeMode === 'exclusive' ? (
              <>
                <Server className="h-4 w-4" />
                {willStartExclusiveServers ? '开机并提交任务' : '使用专享算力生成'}
              </>
            ) : (
              <>
                <Users className="h-4 w-4" />
                使用共享算力生成
              </>
            )}
          </Button>
        ) : (
          <Button asChild size="lg" className="w-full">
            <Link to="/pricing">
              <CreditCard className="h-4 w-4" />
              {selectedComputeMode === 'exclusive' ? '专享余额不足，去充值' : '算力不足，去充值'}
            </Link>
          </Button>
        )}
      </div>

      {showRepeatSubmitCount && (
        <p
          className={cn(
            'text-xs',
            isRepeatSubmitCountValid ? 'text-muted-foreground' : 'text-destructive'
          )}
        >
          {repeatSubmitHint}
        </p>
      )}

      {restrictedSelectionMessage !== null && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {restrictedSelectionMessage}
        </p>
      )}

      {submitError !== null && (
        <p className="text-xs text-destructive">
          {submitError}
        </p>
      )}
    </div>
  )
}

/**
 * 算力模式选择组件属性
 */
interface ComputeModeSectionProps {
  selectedMode: ComputeType
  onModeChange: (mode: ComputeType) => void
  isExclusiveUser: boolean
  disabled: boolean
}

/**
 * 算力模式选择组件
 */
function ComputeModeSection({
  selectedMode,
  onModeChange,
  isExclusiveUser,
  disabled,
}: ComputeModeSectionProps): ReactElement {
  return (
    <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
      {/* 算力模式切换 */}
      <div className="flex items-center gap-2">
        <Label className="text-sm font-medium">算力模式</Label>
        <div className="flex rounded-lg border bg-background p-0.5">
          <button
            type="button"
            disabled={disabled || !isExclusiveUser}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              selectedMode === 'exclusive'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
              !isExclusiveUser && 'cursor-not-allowed opacity-50 hover:text-muted-foreground'
            )}
            onClick={(): void => { onModeChange('exclusive') }}
          >
            <Server className="mr-1 inline-block h-3.5 w-3.5" />
            专享算力
          </button>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              selectedMode === 'shared'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={(): void => { onModeChange('shared') }}
          >
            <Users className="mr-1 inline-block h-3.5 w-3.5" />
            共享算力
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground/80">
        {selectedMode === 'exclusive'
          ? '专享模式下将优先使用你的专享设备，资源更稳定。'
          : '共享模式下任务会进入共享池，按任务消耗算力。'}
      </p>
    </div>
  )
}
